














































































































































import Vue from "vue";
import storeHelper from "../store/store-helper";

import { ContentItemsRequest } from "../types/ContentItemsRequest";
import ContentItemsList from "../components/contentItemsList/ContentItemsList.vue";
import SearchComponent from "../components/generic/SearchComponent.vue";
import { Sort, ContentItem, Portal } from "@infoland/iprova-api";
import { PortalSearchOptions, SearchScope } from "@/types/portal";
import NavDrawerContentItemDetails from "../components/navigation-drawer/NavDrawerContentItemDetails.vue";
import BrowseFilterNavigationDrawer from "../components/navigation-drawer/BrowseFilterNavigationDrawer.vue";
import Toolbar from "../components/toolbar/Toolbar.vue";
import Utils, { PerformanceMeasurement } from "@/utils";
import { Collection } from "../types/collection/collection";
import { Constants } from "../constants";
import { PortalSettings } from "@/types/portal";
import { Controllers } from "@infoland/iprova-api";
import { RecommendedLink } from "@/types/recommendedLink";
import { getPortalForUserWithRedirectAsync } from "@/portals";
import { ApiErrorType } from "@/enums";
import Alert from "../components/alerts/alert.vue";
import { Splitpanes, Pane } from "splitpanes";
import "splitpanes/dist/splitpanes.css";

interface RouteMeta {
    ignoreQuickCode: boolean;
}

type PropsToWatchForInit = {
    searchText: string;
    portalId: number;
    scope: SearchScope;
    collectionIds: number[];
};

export default Vue.extend({
    name: "Browse",
    components: { ContentItemsList, SearchComponent, NavDrawerContentItemDetails, BrowseFilterNavigationDrawer, Toolbar, Alert, Splitpanes, Pane },
    props: {
        searchText: String,
        portalId: Number,
        settingsPortalId: Number,
        scope: String as () => SearchScope,
        collectionIds: Array as () => number[],
        title: String
    },
    data: function () {
        return {
            currentScope: this.scope,
            searchedText: this.searchText ? this.searchText : "",
            showFilterPanel: false,
            searchContentItemsRequest: {} as ContentItemsRequest,
            recommendedLinks: [] as RecommendedLink[],
            loadedContentItems: [] as ContentItem[],
            drawerRight: false,
            currentItem: null as ContentItem,
            searchSuggestionCorrection: null as string,
            currentSearchCollectionIds: [] as number[],
            loadingInProgress: true,
            selectedCollectionId: Constants.Collection.AllCollectionsId as number,
            ignoreSelectedCollectionIdChanges: false,
            reloadFilterFields: false,
            portalHasContentItems: null as boolean,
            portalCollections: [] as Collection[],
            portal: null as Portal,
            errorType: null as ApiErrorType,
            showError: null as Boolean,
            collectionTreeLoaded: false as Boolean,
            toggleOptionChanged: false as Boolean
        };
    },

    beforeRouteEnter(to, from, next) {
        if (from?.name == "Portal") {
            storeHelper.contentItemsStore.reset();
        }

        if (from?.name == "Document") {
            (to.meta as RouteMeta).ignoreQuickCode = true;
        }
        next();
    },
    mounted() {
        this.loadRequiredData();
    },
    computed: {
        totalNumberOfItems(): number {
            let numberOfRecommendedLinks: number = this.searchedText ? this.recommendedLinks.length : 0;
            return storeHelper.contentItemsStore.numberOfAvailableContentItems + numberOfRecommendedLinks;
        },
        showCollections(): boolean {
            return this.isBrowse || this.isCollections;
        },
        changedProps(): PropsToWatchForInit {
            return {
                searchText: this.searchText,
                portalId: this.portalId,
                scope: this.scope,
                collectionIds: this.collectionIds
            };
        },
        contentItems(): ContentItem[] {
            //combine recommended items with the loaded items
            return this.recommendedLinks.map(rc => rc.content_item).concat(this.loadedContentItems);
        },
        mdAndDown(): boolean {
            return this.$vuetify.breakpoint.mdAndDown;
        },
        portalSettings(): PortalSettings {
            if (this.portalId) return this.portal;
            else return storeHelper.portalStore.defaultPortal;
        },
        showIdentifierInTitle(): boolean {
            return this.portal?.show_identifier;
        },
        searchRequested(): boolean {
            return this.searchText && this.searchText.trim().length != 0;
        },
        isSearchingInAllPortals(): boolean {
            return this.scope === "global";
        },
        isBrowse(): boolean {
            return this.$route.name == "Browse";
        },
        isCollections(): boolean {
            return this.$route.name == "Collections";
        },
        isSearch(): boolean {
            return this.$route.name == "Search";
        },
        locationName(): string {
            if (this.title) return this.title;

            if (this.collectionTreeLoaded && !this.portalId && this.collectionIds?.length == 1) {
                return storeHelper.collectionsStore.GetCollection(this.collectionIds[0])?.name;
            }
            return null;
        },
        collections(): Collection[] {
            if (!this.collectionTreeLoaded) return null;
            if (this.isBrowse) {
                return this.portalCollections;
            }

            if (this.isCollections) {
                //get specficic collections
                return this.collectionIds
                    .map(id => {
                        return storeHelper.collectionsStore.GetCollection(id);
                    })
                    .filter(x => x);
            }

            return null;
        }
    },
    watch: {
        changedProps: {
            async handler(newValue: PropsToWatchForInit, oldValue: PropsToWatchForInit): Promise<void> {
                //detect search text change
                if (newValue.searchText && newValue.searchText !== oldValue?.searchText) {
                    if ((this.$route.meta as RouteMeta).ignoreQuickCode === true) {
                        //ignore only once
                        (this.$route.meta as RouteMeta).ignoreQuickCode = false;
                    } else {
                        const quickCodeResult = await Controllers.GenericController.getAsync<ContentItem[]>(
                            `/portals/content_items/?include_content_type=true&include_sub_type_field=true` +
                                `&identifier=${encodeURIComponent(newValue.searchText)}`
                        );

                        if (quickCodeResult && quickCodeResult.length === 1) {
                            Utils.openContentItem(quickCodeResult[0], this.$router, this.portalId);
                            return;
                        }
                    }
                }

                this.init(newValue.portalId !== oldValue?.portalId);
            },
            immediate: true
        },
        currentScope(newScope: SearchScope): void {
            this.$router.replace({ query: { ...this.$route.query, scope: newScope } });
        },
        selectedCollectionId(newSelection: number) {
            if (this.ignoreSelectedCollectionIdChanges) return;

            if (this.portal) {
                //save the selection for later
                storeHelper.portalStore.setSelectedCollection(this.portal.portal_id, newSelection);
            }

            if (this.drawerRight) this.closeDrawerRight();
            this.closeNavFilter();
            this.toggleOptionChanged = !this.toggleOptionChanged;
            this.loadContentItems(false);
        }
    },
    methods: {
        async loadRequiredData() {
            //load collections
            await storeHelper.collectionsStore.loadTree();
            this.collectionTreeLoaded = true;
        },
        async init(portalChanged: boolean) {
            if (portalChanged) {
                //update portal dependend data
                const result = await getPortalForUserWithRedirectAsync(this.portalId, this.$user.user_id, window.location.href);
                this.portal = result.portal;
                this.errorType = result.errorType;
                this.showError = result.showError;
                if (this.portal) {
                    if (this.isSearch) {
                        //don't await this call, the rest of of this function does not depend on the outcome
                        storeHelper.contentItemsStore.getHasContentItemsAsync(this.portalId).then(result => {
                            this.portalHasContentItems = result;
                        });
                    }

                    if (this.isBrowse) {
                        //disable the watch for selected collection ID
                        this.ignoreSelectedCollectionIdChanges = true;

                        //get the default collection states
                        this.portalCollections = await storeHelper.portalStore.getCollections(this.portalId);

                        const selected = storeHelper.portalStore.getSelectedCollectionID(this.portal.portal_id);
                        if (selected) {
                            this.selectedCollectionId = selected;
                        } else {
                            this.selectedCollectionId = Constants.Collection.AllCollectionsId;
                        }

                        //re-enable the watch on selectedCollectionId after $nextTick, elke the watch is executed just after wich would make this disable flag useless
                        this.$nextTick().then(() => {
                            this.ignoreSelectedCollectionIdChanges = false;
                        });
                    } else {
                        this.selectedCollectionId = Constants.Collection.AllCollectionsId;
                    }

                    this.showFilterPanel = false;
                    this.searchContentItemsRequest = {} as ContentItemsRequest;

                    this.loadedContentItems = [] as ContentItem[];
                    this.drawerRight = false;
                    this.currentItem = null as ContentItem;
                    this.searchSuggestionCorrection = null as string;

                    this.loadingInProgress = true;
                    document.title = Utils.getPageTitle(this.portal?.name, "after");
                }
            }
            this.searchedText = this.searchText ? this.searchText : "";
            if (!this.errorType && !this.showError) this.loadContentItems(false);
        },

        doSearch(options: PortalSearchOptions) {
            //prevent change to same route

            if (!(this.searchText == options.text && this.scope == options.scope)) {
                this.$router.replace({
                    query: { ...this.$route.query, searchText: options.text, scope: options.scope }
                });
            }
        },

        onTextBoxSearch(options: PortalSearchOptions) {
            this.searchedText = options.text;
            let includeSearchSuggestionCorrection = options.text ? true : false;
            storeHelper.contentItemsStore.setIncludeSearchSuggestionCorrection(includeSearchSuggestionCorrection);
            this.doSearch(options);
        },

        onUseSearchSuggestedCorrection() {
            storeHelper.contentItemsStore.setIncludeSearchSuggestionCorrection(false);
            this.doSearch({ text: this.searchSuggestionCorrection, scope: this.scope });
        },

        itemClicked(item: ContentItem, recommendedLinkId: string) {
            if (recommendedLinkId) {
                Controllers.GenericController.postAsync<void, void>(`/portals/recommended_links/${recommendedLinkId}/hit`, null); // Fire and forget
            }
            Utils.openContentItem(item, this.$router, this.portalId);
        },
        shouldStillLoadData() {
            return this.loadedContentItems.length < storeHelper.contentItemsStore.numberOfAvailableContentItems;
        },

        async loadContentItems(incremental: boolean): Promise<void> {
            this.loadingInProgress = true;

            let performanceMeasurement = PerformanceMeasurement.Start();

            if (incremental == false) {
                //reset the previously loaded
                this.loadedContentItems = [];
                this.recommendedLinks = [];
            }

            let searchCollectionIds: number[] = null;
            //determine collection ids
            if (this.selectedCollectionId === Constants.Collection.AllCollectionsId) {
                //search ALL collections
                if (this.collectionIds !== null) {
                    //search in all given  collections
                    searchCollectionIds = this.collectionIds;
                }
                //else search in the portal
            } else {
                //set only the selected as collection
                searchCollectionIds = [this.selectedCollectionId];
            }
            this.reloadFilterFields = this.loadedContentItems.length === 0;
            const searchContentItemsRequest = {
                text_contains: this.searchedText,
                include_icons: false,
                include_content_type: true,
                sort: this.isSearch ? Sort.none : Sort.title,
                limit: Constants.Pagination.DefaultLimit,
                offset: this.loadedContentItems.length,
                collection_ids: searchCollectionIds,
                portal_id: this.scope == "portal" ? this.portalId : null, // set portal filter  when scope is portal
                use_settings_from_portal_id: this.settingsPortalId || this.portalId, //determines available filter fields
                is_recommended_link: this.searchText == null ? undefined : false //Skip items that are recomended links when a search text is set
            } as ContentItemsRequest;

            //load items
            const contentItemsPromise = storeHelper.contentItemsStore.loadContentItemsAsync(searchContentItemsRequest);
            let recommendedLinksPromise: Promise<RecommendedLink[]>;
            if (this.searchedText != "") {
                //get recommended links
                recommendedLinksPromise = Controllers.GenericController.getAsync<RecommendedLink[]>(
                    `portals/recommended_links/?text_is_exactly=${encodeURIComponent(
                        this.searchedText
                    )}&include_content_items=true&include_content_types=true&include_sub_types=true`
                );
            } else {
                recommendedLinksPromise = Promise.resolve([]);
            }
            //parrallel load of content items and recommended links
            let [loadContentItemsResult, loadedRecommendedLinks] = await Promise.all([contentItemsPromise, recommendedLinksPromise]);

            if (loadContentItemsResult != null) {
                let results: ContentItem[] = loadContentItemsResult.items;

                //append the results to the previously loaded items
                if (results?.length > 0) {
                    this.loadedContentItems.push(...results);
                }
            }

            if (Array.isArray(loadedRecommendedLinks)) {
                this.recommendedLinks = loadedRecommendedLinks.filter(rc => rc.content_item);
            } else {
                //api returned something different
            }

            performanceMeasurement.AddMetric("resultsLoaded");

            this.loadingInProgress = false;
            this.searchSuggestionCorrection = storeHelper.contentItemsStore.searchSuggestionCorrection;
            if (searchContentItemsRequest.offset === 0 && loadContentItemsResult.filterId) {
                await storeHelper.contentItemFiltersStore.loadFilterFields(loadContentItemsResult.filterId);
                this.reloadFilterFields = false;
            }

            performanceMeasurement.Stop("Load Duration - Browse", searchContentItemsRequest);
        },
        loadContentItemsOnScroll() {
            if (this.shouldStillLoadData() && this.loadingInProgress === false) {
                this.loadContentItems(true);
            }
        },
        filterChanged() {
            if (this.drawerRight) this.closeDrawerRight();
            this.loadContentItems(false);
            this.toggleOptionChanged = !this.toggleOptionChanged;
            this.closeNavFilter();
        },
        async getAutoCompleteItems(val: String) {
            if (this.portalId > Constants.Portal.MaxSystemPortalId) {
                return await storeHelper.suggestionsStore.getSearchSuggestions(this.portalId, val as string);
            } else {
                return [] as Object[];
            }
        },
        openContentItemDetails(item: ContentItem) {
            this.currentItem = item;
            this.drawerRight = true;
        },
        closeDrawerRight() {
            this.drawerRight = false;
        },
        onFilterButtonClicked() {
            this.showFilterPanel = true;
        },
        closeNavFilter() {
            this.showFilterPanel = false;
        },
        getAllCollectionIds(collection: Collection, allColectionsIds: number[]): number[] {
            if (collection && collection.children && collection.children.length > 0) {
                collection.children.forEach(child => {
                    this.getAllCollectionIds(child, allColectionsIds);
                });
            } else {
                allColectionsIds.push(collection.collection_id);
            }
            return allColectionsIds;
        }
    }
});
